import { getLibraryProgressFn } from "@/actions/community/handler";
import {
  listLibraryPathsOptions,
  listPublishedLibraryEntriesOptions,
} from "@/actions/library/handler";
import { trackContentSearch } from "@/components/analytics/conversions";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { seo } from "@/utils/seo";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useRouteContext } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Calendar, Search } from "lucide-react";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/_app/kutuphane/")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(listLibraryPathsOptions()),
      context.queryClient.ensureQueryData(listPublishedLibraryEntriesOptions()),
    ]);
  },
  head: () => ({
    meta: seo({
      title: "Egzersiz Bilimi Kütüphanesi | AtalayTheCoach",
      description:
        "Hipertrofi, kuvvet programlama ve toparlanma üzerine temel bilimsel çalışmaları kronolojik öğrenme yollarıyla okuyun.",
      canonical: "https://atalaythecoach.com/kutuphane",
    }),
  }),
  component: LibraryIndexPage,
});

function LibraryIndexPage() {
  const { data } = useSuspenseQuery(listLibraryPathsOptions());
  const { data: entryData } = useSuspenseQuery(
    listPublishedLibraryEntriesOptions(),
  );
  const { user } = useRouteContext({ from: "/_app" });
  const progress = useQuery({
    queryKey: ["library-progress"],
    queryFn: () => getLibraryProgressFn(),
    enabled: Boolean(user),
  });
  const completed = new Set(progress.data?.completedEntryIds || []);
  const [search, setSearch] = useState("");
  const paths = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("tr-TR");
    if (!query) return data.paths;
    return data.paths.filter(
      (path) =>
        path.title.toLocaleLowerCase("tr-TR").includes(query) ||
        path.description.toLocaleLowerCase("tr-TR").includes(query) ||
        path.entries.some((entry) =>
          entry.turkishTitle.toLocaleLowerCase("tr-TR").includes(query),
        ),
    );
  }, [data.paths, search]);

  return (
    <main className="min-h-screen bg-background pt-28 pb-20 text-foreground">
      <section className="container mx-auto max-w-6xl px-4">
        <div className="max-w-3xl">
          <Badge variant="outline" className="mb-4">
            Açıklamalı bilim rehberi
          </Badge>
          <h1 className="font-extrabold text-4xl tracking-tight sm:text-6xl">
            Egzersiz biliminin temel çalışmalarını sırasıyla keşfet
          </h1>
          <p className="mt-5 text-lg text-muted-foreground leading-8">
            Her öğrenme yolu, temel çalışmalardan güncel bulgulara ilerler. Ne
            bulunduğunu, çalışmanın sınırlarını ve antrenmana nasıl
            aktarılabileceğini birlikte okuyun.
          </p>
        </div>

        <div className="relative mt-10 max-w-xl">
          <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && search.trim()) {
                trackContentSearch("library", search.trim());
              }
            }}
            placeholder="Öğrenme yolu veya kaynak ara"
            className="pl-10"
          />
        </div>

        {paths.length ? (
          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {paths.map((path) => {
              const years = path.entries
                .map((entry) => Number(entry.publicationDate.slice(0, 4)))
                .filter(Number.isFinite);
              const completedCount = path.entries.filter((entry) =>
                completed.has(entry.entryId),
              ).length;
              const completion = path.entries.length
                ? (completedCount / path.entries.length) * 100
                : 0;
              return (
                <Card
                  key={path.pathId}
                  className="flex h-full flex-col overflow-hidden"
                >
                  {path.coverImageUrl && (
                    <img
                      src={path.coverImageUrl}
                      alt=""
                      className="aspect-[16/8] w-full object-cover"
                    />
                  )}
                  <CardHeader>
                    <div className="flex items-center justify-between gap-3 text-muted-foreground text-sm">
                      <span>{path.entries.length} kaynak</span>
                      {years.length > 0 && (
                        <span>
                          {Math.min(...years)}–{Math.max(...years)}
                        </span>
                      )}
                    </div>
                    <CardTitle className="text-2xl">{path.title}</CardTitle>
                    <div className="flex flex-wrap gap-1.5">
                      {path.topics.map((topic) => (
                        <Badge key={topic} variant="secondary">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col">
                    <p className="flex-1 text-muted-foreground leading-7">
                      {path.description}
                    </p>
                    <Progress
                      value={completion}
                      className="mt-6"
                      aria-label="Okuma ilerlemesi"
                    />
                    <Link
                      to="/kutuphane/$pathSlug"
                      params={{ pathSlug: path.slug }}
                      data-analytics-cta="library_path_start"
                      data-analytics-destination={`/kutuphane/${path.slug}`}
                      className="mt-5 inline-flex items-center font-semibold text-primary"
                    >
                      {completedCount
                        ? "Kaldığın yerden devam et"
                        : "Yola başla"}{" "}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="mt-12 rounded-2xl border border-dashed p-10 text-center">
            <BookOpen className="mx-auto h-10 w-10 text-muted-foreground" />
            <h2 className="mt-4 font-semibold text-xl">Sonuç bulunamadı</h2>
            <p className="mt-2 text-muted-foreground">
              Arama ifadenizi değiştirerek tekrar deneyin.
            </p>
          </div>
        )}

        <section className="mt-20 border-t pt-12">
          <div className="max-w-2xl">
            <Badge variant="outline">Kaynak kataloğu</Badge>
            <h2 className="mt-3 font-bold text-3xl">
              Yayımlanmış tüm çalışmalar
            </h2>
            <p className="mt-3 text-muted-foreground">
              Öğrenme yollarından bağımsız olarak bütün açıklamalı kaynaklara
              ulaşın.
            </p>
          </div>
          <div className="mt-7 grid gap-4 md:grid-cols-2">
            {entryData.entries
              .filter((entry) => {
                const query = search.trim().toLocaleLowerCase("tr-TR");
                return (
                  !query ||
                  `${entry.turkishTitle} ${entry.originalTitle} ${entry.authors.join(" ")} ${entry.topics.join(" ")}`
                    .toLocaleLowerCase("tr-TR")
                    .includes(query)
                );
              })
              .map((entry) => (
                <Link
                  key={entry.entryId}
                  to="/kutuphane/kaynak/$entrySlug"
                  params={{ entrySlug: entry.slug }}
                  className="rounded-xl border bg-card p-5 transition-colors hover:border-primary/60"
                >
                  <div className="flex flex-wrap gap-2">
                    {entry.topics.slice(0, 3).map((topic) => (
                      <Badge key={topic} variant="secondary">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                  <h3 className="mt-3 font-semibold text-lg leading-snug">
                    {entry.turkishTitle}
                  </h3>
                  <p className="mt-3 line-clamp-2 text-muted-foreground text-sm">
                    {entry.summary}
                  </p>
                  <span className="mt-4 inline-flex items-center text-muted-foreground text-xs">
                    <Calendar className="mr-1.5 h-3.5 w-3.5" />
                    {entry.publicationDate.slice(0, 4)} · {entry.journal}
                  </span>
                </Link>
              ))}
          </div>
        </section>
      </section>
    </main>
  );
}
